import React from 'react'
import { FormControl } from 'react-bootstrap-five'

// Allows partial numeric entry with at most two decimal places: '', '12',
// '12.', '12.3', '12.34' all pass while '12.345' and non-numeric input are
// rejected, so the field can never hold more than cents precision.
const TWO_DECIMAL_PATTERN = /^\d*(\.\d{0,2})?$/

type Props = {
  value: string
  onChange: (value: string) => void
  testId: string
  ariaLabel: string
  placeholder?: string
  ariaInvalid?: boolean
  ariaDescribedBy?: string
}

// A dollar-denominated text input. Uses type="text" + inputMode="decimal"
// (rather than type="number") so we can fully control the string: cap decimal
// places while typing, normalize to two decimals on blur, and step by a whole
// dollar on Up/Down without the native number spinner snapping to the nearest
// dollar.
const MoneyInput = ({
  value,
  onChange,
  testId,
  ariaLabel,
  placeholder,
  ariaInvalid,
  ariaDescribedBy,
}: Props) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    // Reject the keystroke (keep the previous value) when it would exceed two
    // decimal places or introduce non-numeric characters.
    if (TWO_DECIMAL_PATTERN.test(next)) onChange(next)
  }

  // Normalize to two decimal places when the field loses focus: '12.5' ->
  // '12.50'. An empty field stays empty; a stray '.' clears.
  const handleBlur = () => {
    if (value === '') return
    const parsed = parseFloat(value)
    onChange(Number.isNaN(parsed) ? '' : parsed.toFixed(2))
  }

  // Up/Down step by a whole dollar while preserving cents (12.50 -> 13.50),
  // clamped at zero. We own this because a text input has no native stepper
  // and number-input stepping snaps to the nearest dollar instead of adding
  // one to the entered amount.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    const current = parseFloat(value)
    const base = Number.isNaN(current) ? 0 : current
    const delta = e.key === 'ArrowUp' ? 1 : -1
    onChange(Math.max(0, base + delta).toFixed(2))
  }

  return (
    <div className="promo-currency-input promo-input-narrow">
      <span className="promo-currency-prefix">$</span>
      <FormControl
        type="text"
        inputMode="decimal"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        data-testid={testId}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
      />
    </div>
  )
}

export default MoneyInput
